import { MapUtils } from '../../utility/map-utils.js';
import { isNotNullOrUndefined } from '../../utility/null-safety.js';
import {
  DataModel, DataModelBuilder, SerializationContext, SerializedData,
} from '../dto/data-model.js';

export class NTAKCustomerDetails extends DataModel {
  static readonly TYPE_ID: string = 'a5ac6031a45b4f8a84e1e372b2f25840';

  private constructor(
    /**
     * ISO2 country code.
     * See also: https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2
     */
    public readonly country: string | null,
    /**
     * 4 digit, hungarian postal code.
     */
    public readonly postalCode: string | null,
  ) {
    if(isNotNullOrUndefined(country) && country.length !== 2) {
      throw new Error('Country code must be 2 characters long or null.');
    }

    if(isNotNullOrUndefined(postalCode) && postalCode.length !== 4) {
      throw new Error('Postal code must be 4 characters long or null.');
    }

    if(isNotNullOrUndefined(postalCode) && country !== 'HU') {
      throw new Error('When postal code is set, country must be HU');
    }

    super();
  }

  get $typeId(): string {
    return NTAKCustomerDetails.TYPE_ID;
  }

  get isForeigner(): boolean {
    return this.country !== 'HU';
  }

  static foreigner(country: string | null): NTAKCustomerDetails {
    return this.create({
      country: country,
      postalCode: null,
    });
  }

  static domestic(postalCode: string | null): NTAKCustomerDetails {
    return this.create({
      country: 'HU',
      postalCode: postalCode,
    });
  }

  private static create(args: {
    country: string | null,
    postalCode: string | null,
  }): NTAKCustomerDetails {
    return new NTAKCustomerDetails(
        args.country,
        args.postalCode,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): NTAKCustomerDetails {
    return new NTAKCustomerDetails(
        MapUtils.getStringOrNull(map, 'country'),
        MapUtils.getStringOrNull(map, 'postalCode'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'country': this.country,
      'postalCode': this.postalCode,
    };
  }

  clone(): this {
    return new NTAKCustomerDetails(
        this.country,
        this.postalCode,
    ) as this;
  }

  isSame(other: this): boolean {
    if(this === other) {
      return true;
    }

    return other.country === this.country &&
        other.postalCode === this.postalCode;
  }
}

export class NTAKCustomerDetailsBuilder extends DataModelBuilder<NTAKCustomerDetails> {
  build(context: SerializationContext, map: SerializedData): NTAKCustomerDetails {
    return NTAKCustomerDetails.fromMap(context, map);
  }
}
